import React, { useRef, useState, useEffect } from 'react';
import { PenTool, X, Eraser, Loader2, Undo2, Redo, Palette, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import TamilGrammarAssistantPage from '../../components/grammar/TamilGrammarAssistantPage';
import SignatureCanvas from 'react-signature-canvas';

import { checkWriting } from '../../modules/learning/api/writing';
import { ocrProofreadFile, ocrExtractTextFile } from '../../services/grammarServiceApi';
import { showErrorToast } from '../../utils/errorHandler';
import { mapGrammarSuggestion, normalizeRubric } from '../../types/grammar';
import type { UISuggestion, GrammarRubric } from '../../types/grammar';

export const WritingPage: React.FC = () => {
  // 🌟 AI STATE FOR THE DARK THEME
  const [suggestions, setSuggestions] = useState<UISuggestion[]>([]);
  const [rubric, setRubric] = useState<GrammarRubric | null>(null);
  const [correctedText, setCorrectedText] = useState<string | null>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadingOcrOnly, setLoadingOcrOnly] = useState(false);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const lastRequestIdRef = useRef(0);
  const lastOcrRequestIdRef = useRef(0);

  // 🌟 WHITE iPAD MODAL STATES
  const [isDrawModalOpen, setIsDrawModalOpen] = useState(false);
  const [penColor, setPenColor] = useState('#4f46e5');
  const canvasRef = useRef<SignatureCanvas>(null);
  const colors = ['#000000', '#ef4444', '#22c55e', '#3b82f6', '#a855f7', '#f59e0b'];

  // 🌟 UNDO / REDO STATES
  const [history, setHistory] = useState<any[]>([]);
  const [redoHistory, setRedoHistory] = useState<any[]>([]);

  // 🌟 LISTENER: Opens modal when "Draw" is clicked in the Dark UI
  useEffect(() => {
    const handleOpen = () => setIsDrawModalOpen(true);
    window.addEventListener('open-pro-draw-modal', handleOpen);
    return () => window.removeEventListener('open-pro-draw-modal', handleOpen);
  }, []);

  // 🌟 MAGIC CANVAS LOGIC (Undo, Redo, Clear)
  const handleCanvasEnd = () => {
    if (canvasRef.current) {
      const data = canvasRef.current.toData();
      setHistory(data);
      setRedoHistory([]); 
    }
  };

  const handleUndo = () => {
    if (history.length > 0) {
      const newHistory = history.slice(0, -1);
      const poppedStroke = history[history.length - 1];
      setRedoHistory([...redoHistory, poppedStroke]);
      setHistory(newHistory);
      canvasRef.current?.fromData(newHistory);
    }
  };

  const handleRedo = () => {
    if (redoHistory.length > 0) {
      const strokeToRestore = redoHistory[redoHistory.length - 1];
      const newRedoHistory = redoHistory.slice(0, -1);
      const newHistory = [...history, strokeToRestore];
      setHistory(newHistory);
      setRedoHistory(newRedoHistory);
      canvasRef.current?.fromData(newHistory);
    }
  };

  const handleCanvasClear = () => {
    canvasRef.current?.clear();
    setHistory([]);
    setRedoHistory([]);
  };

  // 🌟 EVALUATE BUTTON (Submits Drawing to API)
  const handleDrawSubmit = async () => {
    if (!canvasRef.current || canvasRef.current.isEmpty()) {
      showErrorToast('Please draw some Tamil letters first!');
      return;
    }
    const canvas = canvasRef.current;
    setLoadingAnalysis(true);
    const requestId = ++lastRequestIdRef.current;
    setSuggestions([]); setRubric(null);

    try {
      const dataURL = canvas.getCanvas().toDataURL('image/png');
      const blob = await (await fetch(dataURL)).blob();
      const file = new File([blob], 'handwriting.png', { type: 'image/png' });

      const res = await ocrProofreadFile(file);
      if (requestId !== lastRequestIdRef.current) return;

      if (res.text) {
        setEditorContent(res.text); // Auto-types text into the Dark UI
      }
      
      setSuggestions((res.suggestions || []).map(mapGrammarSuggestion));
      setRubric(res.rubric != null ? normalizeRubric(res.rubric) : null);
      
      setIsDrawModalOpen(false); 
      handleCanvasClear(); // Clean up for the next time it opens

    } catch (err: unknown) {
      if (requestId !== lastRequestIdRef.current) return;
      showErrorToast('AI busy. Please try again later.');
    } finally {
      if (requestId === lastRequestIdRef.current) setLoadingAnalysis(false);
    }
  };

  // --- DARK THEME TEXT LOGIC ---
  const handleCheckGrammar = async (text: string) => {
    if (!text.trim()) { showErrorToast('Please type something to check.'); return; }
    const requestId = ++lastRequestIdRef.current;
    setSuggestions([]); setRubric(null); setCorrectedText(null); setFocusedId(null); setLoadingAnalysis(true);
    try {
      const res = await checkWriting({ text, max_suggestions: 8, mode: 'practice' });
      if (requestId !== lastRequestIdRef.current) return;
      setSuggestions((res.suggestions || []).map(mapGrammarSuggestion));
      setRubric(res.rubric != null ? normalizeRubric(res.rubric) : null);
      setCorrectedText(res.text ?? null);
    } catch (err: unknown) {
      if (requestId !== lastRequestIdRef.current) return;
      showErrorToast('Error checking grammar');
    } finally {
      if (requestId === lastRequestIdRef.current) setLoadingAnalysis(false);
    }
  };

  const handleUploadFile = async (file: File) => {
    const requestId = ++lastRequestIdRef.current;
    setSuggestions([]); setRubric(null); setCorrectedText(null); setFocusedId(null); setLoadingAnalysis(true);
    try {
      const res = await ocrProofreadFile(file);
      if (requestId !== lastRequestIdRef.current) return;
      setSuggestions((res.suggestions || []).map(mapGrammarSuggestion));
      setRubric(res.rubric != null ? normalizeRubric(res.rubric) : null);
      setCorrectedText(res.text ?? null);
      if (res.text != null) setEditorContent(res.text);
    } catch (err: unknown) {
      if (requestId !== lastRequestIdRef.current) return;
      showErrorToast('Error uploading file');
    } finally {
      if (requestId === lastRequestIdRef.current) setLoadingAnalysis(false);
    }
  };

  const handleUploadOcrOnly = async (file: File) => {
    const requestId = ++lastOcrRequestIdRef.current;
    setLoadingOcrOnly(true);
    try {
      const res = await ocrExtractTextFile(file);
      if (requestId !== lastOcrRequestIdRef.current) return;

      const extracted = (res.text ?? '').trim();
      if (!extracted) {
        showErrorToast('No text detected in image');
        return;
      }

      // Update only editor content and clear stale analysis state
      setEditorContent(extracted);
      setSuggestions([]);
      setRubric(null);
      setCorrectedText(null);
      setFocusedId(null);
    } catch (err: unknown) {
      if (requestId !== lastOcrRequestIdRef.current) return;
      showErrorToast('Error extracting text');
    } finally {
      if (requestId === lastOcrRequestIdRef.current) setLoadingOcrOnly(false);
    }
  };

  const handleClearAssistant = () => {
    setEditorContent(''); setSuggestions([]); setRubric(null); setCorrectedText(null); setFocusedId(null);
    if (typeof window !== 'undefined') { try { window.localStorage.removeItem('tamil_editor_content'); } catch {} }
  };

  const handleApplySuggestion = (suggestion: UISuggestion) => {
    setEditorContent((prev) => {
      const idx = prev.indexOf(suggestion.original);
      if (idx === -1) return prev;
      return prev.slice(0, idx) + suggestion.suggestion + prev.slice(idx + suggestion.original.length);
    });
  };

  return (
    <div className="w-full h-full relative">
      {/* 🌟 YOUR CLEAN DARK UI 🌟 */}
      <TamilGrammarAssistantPage
        editorContent={editorContent}
        onEditorContentChange={setEditorContent}
        suggestions={suggestions}
        rubric={rubric}
        correctedText={correctedText}
        focusedId={focusedId}
        onFocusSuggestion={setFocusedId}
        onCheckGrammar={handleCheckGrammar}
        onUploadFile={handleUploadFile}
        onUploadOcrOnly={handleUploadOcrOnly}
        loadingOcrOnly={loadingOcrOnly}
        onClear={handleClearAssistant}
        isLoading={loadingAnalysis}
        onApplySuggestion={handleApplySuggestion}
      />

      {/* 🌟 THE BRIGHT WHITE iPAD POPUP 🌟 */}
      {isDrawModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-slate-900/70 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-5xl h-[85vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden border border-slate-200">
            
            {/* Header */}
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center z-10">
              <div className="flex items-center gap-6">
                <h3 className="text-xl font-black text-slate-800 flex items-center gap-2">
                  <PenTool className="w-6 h-6 text-indigo-600" /> Magic Whiteboard
                </h3>
                <div className="flex items-center gap-2 bg-white p-2 rounded-full border border-slate-200 shadow-sm">
                  <Palette className="w-4 h-4 text-slate-400 ml-2" />
                  {colors.map(c => (
                    <button 
                      key={c} 
                      onClick={() => setPenColor(c)} 
                      className={`w-6 h-6 rounded-full transition-transform border border-slate-200 ${penColor === c ? 'scale-125 ring-2 ring-indigo-500 shadow-md' : 'hover:scale-110'}`} 
                      style={{ backgroundColor: c }} 
                    />
                  ))}
                </div>
              </div>
              <button onClick={() => setIsDrawModalOpen(false)} className="p-2 hover:bg-slate-200 rounded-full text-slate-500 transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Canvas Area */}
            <div className="flex-1 bg-[url('https://www.transparenttextures.com/patterns/notebook.png')] bg-white relative cursor-crosshair">
              <SignatureCanvas 
                ref={canvasRef} 
                penColor={penColor} 
                minWidth={2} 
                maxWidth={5} 
                onEnd={handleCanvasEnd}
                canvasProps={{ className: 'w-full h-full absolute inset-0' }} 
              />
            </div>

            {/* Footer Actions */}
            <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-between items-center">
              <div className="flex gap-3">
                <Button variant="outline" size="lg" onClick={handleUndo} disabled={history.length === 0} className="border-slate-300 text-slate-700 hover:bg-slate-100 font-bold px-6 bg-white shadow-sm disabled:opacity-50">
                  <Undo2 className="w-5 h-5 mr-2" /> Undo
                </Button>
                <Button variant="outline" size="lg" onClick={handleRedo} disabled={redoHistory.length === 0} className="border-slate-300 text-slate-700 hover:bg-slate-100 font-bold px-6 bg-white shadow-sm disabled:opacity-50 hidden sm:flex">
                  <Redo className="w-5 h-5 mr-2" /> Redo
                </Button>
                <Button variant="outline" size="lg" onClick={handleCanvasClear} className="border-rose-200 text-rose-600 hover:bg-rose-50 font-bold px-6 bg-white shadow-sm">
                  <Eraser className="w-5 h-5 mr-2" /> Clear
                </Button>
              </div>
              <Button size="lg" onClick={handleDrawSubmit} disabled={loadingAnalysis} className="bg-indigo-600 hover:bg-indigo-700 text-white font-black px-10 py-6 text-lg shadow-lg shadow-indigo-600/30">
                {loadingAnalysis ? <Loader2 className="w-6 h-6 animate-spin mr-2" /> : <Sparkles className="w-6 h-6 mr-2" />}
                {loadingAnalysis ? "Evaluating..." : "Evaluate Grammar ✨"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WritingPage;